import React, { ReactNode } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import LoginPage from './AuthComponents/LoginPage';
import WorkosCallbackPage from './AuthComponents/WorkosCallbackPage';
import ImpersonationLanding from './AuthComponents/ImpersonationLanding';
import DevLoginPage from './AuthComponents/DevLoginPage';
import ImpersonationBanner from './AuthComponents/ImpersonationBanner';
import { getToken } from './utils/TokenUtils';
import Dashboard from './Dashboard';
import { ThemeProvider } from './Components/Custom/ThemeProvider';
import { CommandPaletteProvider } from './Components/Shell/CommandPaletteProvider';
import ViolationDetailPage from './Violations/ViolationDetailPage';
import ViolationPage from './Violations/ViolationPage';
import NotificationPage from './Notifications/NotificationPage';
import NotificationDetailPage from './Notifications/NotificationDetailPage';
import SettingsPage from './SettingsPage';
import AccountPage from './Account/AccountPage';
import ActivityPage from './Account/ActivityPage';
import StatementsPage from './Account/StatementsPage';
import StatementDetailPage from './Account/StatementDetailPage';
import AmenitiesPage from './Reservations/AmenitiesPage';
import AmenityDetailPage from './Reservations/AmenityDetailPage';
import GroupBookingPage from './Reservations/GroupBookingPage';
import MyReservationsPage from './Reservations/MyReservationsPage';
import ReservationDetailPage from './Reservations/ReservationDetailPage';
import NotFoundPage from './NotFoundPage';

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const token = getToken();

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      {/* One ⌘K palette for the whole signed-in app (#603). */}
      <CommandPaletteProvider>
      <div className="min-h-screen bg-stone-300 dark:bg-gray-900">
        {/*
          Outside <Routes> on purpose: a manager viewing a homeowner's
          account must see that they are, on every page, not only the one
          they landed on.
        */}
        <ImpersonationBanner />
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          {/*
            Where AuthKit returns the browser. Public, because the member
            has no token yet — the code in the query string is what the
            callback trades for one.

            `/forgot-password` and `/reset-password` are gone: WorkOS owns
            password recovery now, on its own hosted pages. Anyone holding
            an old bookmark falls through to the catch-all below and lands
            on the sign-in door, which is where they wanted to go anyway.
          */}
          <Route path="/callback" element={<WorkosCallbackPage />} />
          {/*
            Where front-porch sends a manager opening a read-only view of
            a homeowner's portal. Public for the same reason `/callback`
            is: the token arrives *in* the request, so there is nothing to
            protect the route with yet.
          */}
          <Route path="/impersonate" element={<ImpersonationLanding />} />
          {/*
            Password sign-in for environments AuthKit cannot serve.
            **Not linked from `/login`** — typed by hand, like the
            management realm's. Renders no form unless
            `GET /member-portal/auth/dev-login` answers, and that route
            exists only below `prod`.
          */}
          <Route path="/dev-login" element={<DevLoginPage />} />

          {/* Protected routes */}
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <Dashboard currentPage="home" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <AccountPage currentPage="account" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account/activity"
            element={
              <ProtectedRoute>
                <ActivityPage currentPage="account" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account/statements"
            element={
              <ProtectedRoute>
                <StatementsPage currentPage="account" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account/statements/:period"
            element={
              <ProtectedRoute>
                <StatementDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <NotificationPage currentPage="notifications" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notification/:notificationId"
            element={
              <ProtectedRoute>
                <NotificationDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/violations"
            element={
              <ProtectedRoute>
                <ViolationPage currentPage="violations" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/violation/:violationId"
            element={
              <ProtectedRoute>
                <ViolationDetailPage />
              </ProtectedRoute>
            }
          />
          {/* Amenity booking (#643): the portal's first create flow. */}
          <Route
            path="/amenities"
            element={
              <ProtectedRoute>
                <AmenitiesPage currentPage="amenities" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/amenities/:amenityId"
            element={
              <ProtectedRoute>
                <AmenityDetailPage />
              </ProtectedRoute>
            }
          />
          {/* "Any court will do" — book whichever amenity in a group is
              free (#640 phase 3). */}
          <Route
            path="/amenities/groups/:groupId"
            element={
              <ProtectedRoute>
                <GroupBookingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reservations"
            element={
              <ProtectedRoute>
                <MyReservationsPage currentPage="reservations" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reservations/:reservationId"
            element={
              <ProtectedRoute>
                <ReservationDetailPage />
              </ProtectedRoute>
            }
          />
          {/* The sidebar has always linked to these two. Neither was a
              route, so both fell through to `*` and bounced the member
              back to /login. */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-profile"
            element={<Navigate to="/settings" replace />}
          />

          {/* Redirect root to home for authenticated users, login for others */}
          <Route
            path="/"
            element={
              getToken() ? (
                <Navigate to="/home" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          {/* An unknown URL is a 404 once signed in (#652); signed out,
              the guard still sends it to the sign-in door. */}
          <Route
            path="*"
            element={
              <ProtectedRoute>
                <NotFoundPage />
              </ProtectedRoute>
            }
          />
        </Routes>
      </div>
      </CommandPaletteProvider>
    </ThemeProvider>
  );
};

export default App;
